import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  Avatar,
  Skeleton,
  Stack,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';

/**
 * LinkInBioPreview - Prévia em tempo real da página Link in Bio
 * 
 * TODOs implementados:
 * 9.1 - Renderização inicial com título, subtítulo, links
 * 9.2 - Aplicação de estilos customizados (cores, fonts)
 * 9.3 - Suporte a múltiplos templates
 */

const LinkInBioPreview = ({
  formData = {},
  templates = [],
  loading = false,
}) => {
  // Determine template layout
  const currentTemplate = templates.find(t => t.id === formData.templateId);
  const templateType = currentTemplate?.name || 'default';
  
  // Log template change
  useEffect(() => {
    if (formData.templateId) {
      console.log('[LinkInBioPreview] Template changed:', {
        templateId: formData.templateId,
        templateName: currentTemplate?.name,
      });
    }
  }, [formData.templateId, currentTemplate]);
  // Estilos customizados
  const customStyles = formData.customStyles || {};
  
  // Map fontSize names to actual pixel values
  const fontSizeMap = {
    'small': '14px',
    'medium': '16px',
    'large': '18px',
    'extra-large': '20px',
  };
  
  const containerStyle = {
    backgroundColor: customStyles.backgroundColor || '#ffffff',
    color: customStyles.textColor || '#000000',
    fontFamily: customStyles.fontFamily || 'Arial',
    fontSize: fontSizeMap[customStyles.fontSize] || fontSizeMap['medium'],
  };

  const linkStyle = {
    backgroundColor: customStyles.linkBackground || '#f0f0f0',
    color: customStyles.linkColor || '#1976d2',
    fontFamily: customStyles.fontFamily || 'Arial',
    fontSize: fontSizeMap[customStyles.fontSize] || fontSizeMap['medium'],
  };

  const agendaButtonStyle = {
    color: customStyles.linkColor || '#1976d2',
    borderColor: customStyles.linkColor || '#1976d2',
    textTransform: 'none',
    fontWeight: 600,
    fontFamily: customStyles.fontFamily || 'Arial',
    fontSize: fontSizeMap[customStyles.fontSize] || fontSizeMap['medium'],
  };

  // Ícones de redes sociais
  const socialIcons = {
    instagram: InstagramIcon,
    twitter: TwitterIcon,
    linkedin: LinkedInIcon,
    youtube: YouTubeIcon,
    threads: LinkIcon,
    facebook: FacebookIcon,
  };

  const socialLinks = formData.socialLinks || {};
  const genericLinks = formData.genericLinks || [];

  // Filtrar links sociais preenchidos
  const activeSocialLinks = useMemo(() => {
    const filtered = Object.entries(socialLinks)
      .filter(([_, data]) => {
        // Verifica se data é um objeto com url, ou uma string
        const url = typeof data === 'object' ? data?.url : data;
        return url && url.trim() !== '';
      })
      .map(([key, data]) => {
        const url = typeof data === 'object' ? data.url : data;
        return {
          key,
          label: key.charAt(0).toUpperCase() + key.slice(1),
          url,
          icon: socialIcons[key],
        };
      });
    
    return filtered;
  }, [socialLinks]);

  // Filtrar links genéricos preenchidos
  const activeGenericLinks = useMemo(() => {
    const filtered = genericLinks.filter((link) => {
      const hasUrl = link && link.url && link.url.trim() !== '';
      const hasName = link && link.name && link.name.trim() !== '';
      return hasUrl && hasName;
    });
    
    if (genericLinks.length > 0 && filtered.length === 0) {
      console.log('[LinkInBioPreview] DEBUG genericLinks filter:', {
        total: genericLinks.length,
        genericLinks: genericLinks,
        filtered: filtered,
      });
    }
    
    return filtered;
  }, [genericLinks]);

  // Log preview state - useful for debugging data flow
  useEffect(() => {
    const socialCount = Object.values(formData.socialLinks || {}).filter(l => l?.url).length;
    const genericCount = (formData.genericLinks || []).filter(l => l?.url).length;
    
    if (socialCount > 0 || genericCount > 0 || formData.templateId) {
      console.log('[LinkInBioPreview] ✓ Preview updated:', {
        template: currentTemplate?.name || 'none',
        socialLinks: socialCount,
        genericLinks: genericCount,
      });
    }
  }, [formData.socialLinks, formData.genericLinks, formData.templateId, currentTemplate]);

  // Determine if this is "Agenda Primeiro" template (ID: 33333333-3333-3333-3333-333333333333)
  const isAgendaPrimeiro = formData.templateId === '33333333-3333-3333-3333-333333333333';
  const isLinksSimples = formData.templateId === '11111111-1111-1111-1111-111111111111';
  const isComIconesSociais = formData.templateId === '22222222-2222-2222-2222-222222222222';
  const isSocialPlusAgenda = formData.templateId === '44444444-4444-4444-4444-444444444444';

  /**
   * Helper function: Render social links as rectangular cards (for Links Simples and Agenda Primeiro)
   */
  const renderSocialLinksAsCards = () => (
    <Stack spacing={1.5}>
      {activeSocialLinks.map(({ key, label, url, icon: Icon }) => (
        <Button
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          variant="contained"
          sx={{
            ...linkStyle,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            borderRadius: 2,
            '&:hover': {
              opacity: 0.8,
            },
          }}
          startIcon={<Icon sx={{ fontSize: 18 }} />}
        >
          {label}
        </Button>
      ))}
    </Stack>
  );

  /**
   * Helper function: Render social links as side-by-side icons (for Com Ícones Sociais and Social + Agenda)
   */
  const renderSocialLinksAsIcons = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
      {activeSocialLinks.map(({ key, label, url, icon: Icon }) => (
        <IconButton
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            ...linkStyle,
            borderRadius: '50%',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          size="small"
          title={label}
        >
          <Icon sx={{ fontSize: 20 }} />
        </IconButton>
      ))}
    </Box>
  );

  /**
   * Render the content based on template type
   */
  const renderContent = () => {
    // TEMPLATE 1: Links Simples
    // All links (social + generic) as cards, agenda at bottom
    if (isLinksSimples) {
      return (
        <>
          {/* Social links as cards */}
          {activeSocialLinks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {renderSocialLinksAsCards()}
            </Box>
          )}

          {/* Generic links as cards */}
          {activeGenericLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1.5}>
                {activeGenericLinks.map((link, index) => (
                  <Button
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    sx={{
                      ...linkStyle,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    startIcon={<LinkIcon />}
                  >
                    {link.name}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {/* Agenda button - at bottom */}
          {formData.scheduleId && (
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Button
                fullWidth
                variant="outlined"
                sx={{
                  color: customStyles.linkColor || '#1976d2',
                  borderColor: customStyles.linkColor || '#1976d2',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Agendar
              </Button>
            </Box>
          )}

          {/* Empty message */}
          {activeSocialLinks.length === 0 && activeGenericLinks.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: customStyles.textColor || '#000000',
                opacity: 0.5,
                mt: 3,
              }}
            >
              Adicione links para visualizar aqui
            </Typography>
          )}
        </>
      );
    }

    // TEMPLATE 2: Com Ícones Sociais
    // Social links as icons (side-by-side), generic links as cards, agenda at bottom
    if (isComIconesSociais) {
      return (
        <>
          {/* Social links as icons */}
          {activeSocialLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {renderSocialLinksAsIcons()}
            </Box>
          )}

          {/* Generic links as cards */}
          {activeGenericLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1.5}>
                {activeGenericLinks.map((link, index) => (
                  <Button
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    sx={{
                      ...linkStyle,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    startIcon={<LinkIcon />}
                  >
                    {link.name}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {/* Agenda button - at bottom */}
          {formData.scheduleId && (
            <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              <Button
                fullWidth
                variant="outlined"
                sx={agendaButtonStyle}
              >
                Agendar
              </Button>
            </Box>
          )}

          {/* Empty message */}
          {activeSocialLinks.length === 0 && activeGenericLinks.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: customStyles.textColor || '#000000',
                opacity: 0.5,
                mt: 3,
              }}
            >
              Adicione links para visualizar aqui
            </Typography>
          )}
        </>
      );
    }

    // TEMPLATE 3: Agenda Primeiro
    // Agenda button at TOP, social links as cards, generic links as cards
    if (isAgendaPrimeiro) {
      return (
        <>
          {/* Agenda button - at TOP */}
          {formData.scheduleId && (
            <Box sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                sx={agendaButtonStyle}
              >
                Agendar
              </Button>
            </Box>
          )}

          {/* Social links as cards */}
          {activeSocialLinks.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {renderSocialLinksAsCards()}
            </Box>
          )}

          {/* Generic links as cards */}
          {activeGenericLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1.5}>
                {activeGenericLinks.map((link, index) => (
                  <Button
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    sx={{
                      ...linkStyle,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    startIcon={<LinkIcon />}
                  >
                    {link.name}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {/* Empty message */}
          {activeSocialLinks.length === 0 && activeGenericLinks.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: customStyles.textColor || '#000000',
                opacity: 0.5,
                mt: 3,
              }}
            >
              Adicione links para visualizar aqui
            </Typography>
          )}
        </>
      );
    }

    // TEMPLATE 4: Social + Agenda
    // Social links as icons, agenda button, generic links as cards
    if (isSocialPlusAgenda) {
      return (
        <>
          {/* Social links as icons */}
          {activeSocialLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {renderSocialLinksAsIcons()}
            </Box>
          )}

          {/* Agenda button - in the middle */}
          {formData.scheduleId && (
            <Box sx={{ mb: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                sx={agendaButtonStyle}
              >
                Agendar
              </Button>
            </Box>
          )}

          {/* Generic links as cards */}
          {activeGenericLinks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1.5}>
                {activeGenericLinks.map((link, index) => (
                  <Button
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    sx={{
                      ...linkStyle,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                    startIcon={<LinkIcon />}
                  >
                    {link.name}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}

          {/* Empty message */}
          {activeSocialLinks.length === 0 && activeGenericLinks.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: customStyles.textColor || '#000000',
                opacity: 0.5,
                mt: 3,
              }}
            >
              Adicione links para visualizar aqui
            </Typography>
          )}
        </>
      );
    }

    // Default / fallback (shouldn't happen)
    return null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: '500px',
        }}
      >
        <Stack spacing={2} sx={{ width: '100%', maxWidth: 400, p: 2 }}>
          <Skeleton variant="circular" width={100} height={100} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="rectangular" height={40} />
          <Skeleton variant="rectangular" height={40} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '100%',
        p: 2,
        bgcolor: '#e8e8e8',
        overflowY: 'auto',
      }}
    >
      {/* Container simulando dispositivo mobile */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          borderRadius: 3,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          ...containerStyle,
        }}
      >
        {/* Header/Conteúdo */}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {/* Avatar/Ícone */}
          {formData.logoUrl ? (
            // Avatar com imagem
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                borderRadius: formData.avatarShape === 'round' ? '50%' : 1,
                overflow: 'hidden',
                border: '3px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
              }}
            >
              <img
                src={formData.logoUrl}
                alt={formData.title || 'Avatar'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: formData.avatarShape === 'round' ? 'cover' : 'contain',
                  padding: formData.avatarShape === 'round' ? 0 : 4,
                }}
              />
            </Box>
          ) : (
            // Avatar com inicial (fallback)
            <Avatar
              sx={{
                width: 80,
                height: 80,
                mx: 'auto',
                mb: 2,
                bgcolor: customStyles.linkColor || '#1976d2',
                fontSize: '2rem',
              }}
            >
              {formData.title?.charAt(0).toUpperCase() || 'L'}
            </Avatar>
          )}

          {/* Título */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: customStyles.textColor || '#000000',
            }}
          >
            {formData.title || 'Seu Título Aqui'}
          </Typography>

          {/* Subtítulo */}
          {formData.subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: customStyles.textColor || '#000000',
                opacity: 0.8,
                mb: 3,
              }}
            >
              {formData.subtitle}
            </Typography>
          )}

          {/* Render content based on template */}
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default LinkInBioPreview;
